import { CSSProperties, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const SCROLL_CONTAINER_SELECTOR = '[data-virtual-scroll]';
const DROPDOWN_OFFSET = 4;

interface UseScrollContainerDropdownPositionOptions {
  anchorRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
}

export const useScrollContainerDropdownPosition = ({
  anchorRef,
  isOpen,
  onClose,
}: UseScrollContainerDropdownPositionOptions) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [positionStyle, setPositionStyle] = useState<CSSProperties>({});
  const [isInline, setIsInline] = useState(false);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      onClose();
      return;
    }

    const scrollRoot = anchor.closest<HTMLElement>(SCROLL_CONTAINER_SELECTOR);

    if (!scrollRoot) {
      setIsInline(true);
      setPortalTarget(null);
      setPositionStyle({});
      return;
    }

    setIsInline(false);
    setPortalTarget(scrollRoot);

    const anchorRect = anchor.getBoundingClientRect();
    const rootRect = scrollRoot.getBoundingClientRect();

    setPositionStyle({
      position: 'absolute',
      top: anchorRect.bottom - rootRect.top + scrollRoot.scrollTop + DROPDOWN_OFFSET,
      left: anchorRect.left - rootRect.left + scrollRoot.scrollLeft,
      zIndex: 10,
    });
  }, [anchorRef, onClose]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPortalTarget(null);
      setIsInline(false);
      setPositionStyle({});
      return;
    }

    updatePosition();

    const scrollRoot = anchorRef.current?.closest<HTMLElement>(SCROLL_CONTAINER_SELECTOR);

    scrollRoot?.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollRoot?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition, anchorRef]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (anchorRef.current?.contains(target)) {
        return;
      }

      if (menuRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, anchorRef, onClose]);

  return { menuRef, portalTarget, positionStyle, isInline };
};
